import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useState } from "react";

import { useAtom, useSetAtom } from "../../app-jotai";
import {
  authDialogAtom,
  currentUserAtom,
  workspaceApi,
  WorkspaceApiError,
} from "../../data/workspace";

import "./Workspace.scss";

type Mode = "login" | "register";

export const AuthDialog = () => {
  const [isOpen, setOpen] = useAtom(authDialogAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setError(null);
    setPassword("");
  };

  const submit = async () => {
    setError(null);
    try {
      const { user } =
        mode === "login"
          ? await workspaceApi.login(email.trim(), password)
          : await workspaceApi.register(
              email.trim(),
              password,
              displayName.trim() || undefined,
            );
      setCurrentUser(user);
      close();
    } catch (err) {
      setError(
        err instanceof WorkspaceApiError
          ? err.message
          : "Something went wrong. Is the workspace backend reachable?",
      );
    }
  };

  // Keep the canvas's global shortcuts (e.g. Backspace = delete element) from
  // stealing keystrokes while typing in the dialog. Escape is allowed through so
  // the Dialog/Modal can still close.
  const stopCanvasKeys = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") {
      e.stopPropagation();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    stopCanvasKeys(e);
    if (e.key === "Enter" && email && password) {
      submit();
    }
  };

  return (
    <Dialog
      size="small"
      onCloseRequest={close}
      title={
        mode === "login" ? "Sign in to your workspace" : "Create an account"
      }
    >
      <div
        className="WorkspaceDialog WorkspaceAuth"
        onKeyDown={onKeyDown}
        onKeyUp={stopCanvasKeys}
      >
        {mode === "register" && (
          <div className="WorkspaceField">
            <label htmlFor="ws-name">Display name</label>
            <input
              id="ws-name"
              type="text"
              autoComplete="name"
              value={displayName}
              placeholder="Ada Lovelace"
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
        )}
        <div className="WorkspaceField">
          <label htmlFor="ws-email">Email</label>
          <input
            id="ws-email"
            type="email"
            autoComplete="email"
            value={email}
            placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="WorkspaceField">
          <label htmlFor="ws-password">Password</label>
          <input
            id="ws-password"
            type="password"
            autoComplete={
              mode === "login" ? "current-password" : "new-password"
            }
            value={password}
            placeholder="••••••••"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <p className="Workspace__error">{error}</p>}

        <FilledButton
          size="large"
          fullWidth
          label={mode === "login" ? "Sign in" : "Create account"}
          onClick={submit}
        />

        <button
          type="button"
          className="WorkspaceAuth__toggle"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError(null);
          }}
        >
          {mode === "login"
            ? "Don't have an account? Create one"
            : "Already have an account? Sign in"}
        </button>
      </div>
    </Dialog>
  );
};
